/**
 * The referral review queue.
 *
 * Every item shows the specific evidence rather than a verdict, because the owner has to be
 * able to tell a family sharing an address from a person referring themselves. Contact
 * details are masked; the payment handle shows its last four, which is enough to see that
 * two people paid from the same one without putting the handle on screen.
 *
 * Approve and deny are tier 2. Both go through the confirm dialog with a required reason,
 * and the approve button is disabled outright when a hard signal fired, because a referee
 * who already had a paid order is not a referral under any reading of the program.
 */
import { useState } from 'react';
import { Button, Card, ConfirmDialog, EmptyState, ErrorState, MoneyText, Pill, Skeleton } from '@/components';
import { formatMoney } from '@/lib/format';
import { useReferrals, useReviewReferral } from './api';
import type { ReferralRow } from './types';
import '@/features/finance/finance.css';

export function ReferralsPage() {
  const query = useReferrals('queue');
  const review = useReviewReferral();
  const [pending, setPending] = useState<{ row: ReferralRow; decision: 'approve' | 'deny' } | null>(null);

  if (query.isLoading) return <Skeleton shape="card" />;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="The referral queue could not be loaded."
        onRetry={() => {
          void query.refetch();
        }}
      />
    );
  }

  const data = query.data;
  const breached = data.items.filter((row) => row.sla.breached).length;

  return (
    <div className="o-stack">
      <Card
        title="Referral review queue"
        basis="ledger"
        action={
          <span style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Pill tone={data.items.length === 0 ? 'ok' : data.items.length > 10 ? 'warn' : 'neutral'}>
              {`${data.items.length} waiting`}
            </Pill>
            {breached > 0 ? <Pill tone="crit">{`${breached} past the ${data.sla_hours}h SLA`}</Pill> : null}
          </span>
        }
      >
        {data.items.length === 0 ? (
          <EmptyState message="Nothing waiting for review." />
        ) : (
          data.items.map((row) => (
            <ReferralCard
              key={row.referral_id}
              row={row}
              onDecide={(decision) => setPending({ row, decision })}
              busy={review.isPending}
            />
          ))
        )}
        <p className="o-muted">{data.note}</p>
      </Card>

      <ConfirmDialog
        open={pending !== null}
        title={
          pending
            ? `${pending.decision === 'approve' ? 'Approve' : 'Deny'} referral ${pending.row.referral_id}?`
            : ''
        }
        body={
          pending?.decision === 'approve'
            ? 'This issues the reward as store credit to the referrer.'
            : 'This records the denial. No credit is issued.'
        }
        effects={
          pending?.decision === 'approve'
            ? [
                `Issues ${formatMoney(pending.row.reward_cents)} of store credit to the referrer`,
                'Writes an audit row with the fraud score and every signal that fired',
                'Pushes the decision to WordPress through the bridge',
              ]
            : ['Records the denial with your reason', 'Writes an audit row', 'No credit is issued']
        }
        confirmLabel={pending?.decision === 'approve' ? 'Approve and issue' : 'Deny'}
        destructive={pending?.decision === 'deny'}
        reasonRequired
        busy={review.isPending}
        onCancel={() => setPending(null)}
        onConfirm={async ({ reason }) => {
          if (!pending) return;
          await review.mutateAsync({
            id: pending.row.referral_id,
            decision: pending.decision,
            reason: reason ?? 'reviewed',
            seen_score: pending.row.fraud_score,
          });
          setPending(null);
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function bandTone(band: ReferralRow['band']): 'ok' | 'warn' | 'crit' {
  return band === 'auto_approve' ? 'ok' : band === 'review' ? 'warn' : 'crit';
}

export function ReferralCard({
  row,
  onDecide,
  busy,
}: {
  row: ReferralRow;
  onDecide: (decision: 'approve' | 'deny') => void;
  busy: boolean;
}) {
  return (
    <div className="o-kv" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ minWidth: 0, flex: '1 1 60%' }}>
        <div>
          <b>{row.referee_email_masked}</b>{' '}
          {row.order_id ? <span className="o-muted">order {row.order_id}</span> : null}{' '}
          <Pill tone={bandTone(row.band)}>{`score ${row.fraud_score}`}</Pill>{' '}
          {row.sla.breached ? (
            <Pill tone="crit">{`${Math.round(row.sla.hours)}h, past SLA`}</Pill>
          ) : row.sla.at_risk ? (
            <Pill tone="warn">{`${Math.round(row.sla.hours)}h`}</Pill>
          ) : (
            <Pill tone="neutral">{`${Math.round(row.sla.hours)}h`}</Pill>
          )}
        </div>
        {row.signals.length === 0 ? (
          <p className="o-muted">No fraud signals fired.</p>
        ) : (
          <ul className="o-signals">
            {row.signals.map((signal) => (
              <li key={signal.code} className="o-signals__row">
                <Pill tone={signal.hard ? 'crit' : 'warn'}>{signal.hard ? 'hard' : String(signal.weight)}</Pill>
                <span>{signal.label}</span>
                <span className="o-signals__evidence">{signal.evidence}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
        <span className="o-kv__value">
          <MoneyText cents={row.reward_cents} showCents />
        </span>
        <Button
          size="sm"
          busy={busy}
          disabled={row.auto_deny}
          title={row.auto_deny ? 'A hard signal fired. This cannot be approved.' : undefined}
          onClick={() => onDecide('approve')}
        >
          Approve
        </Button>
        <Button size="sm" variant="danger" busy={busy} onClick={() => onDecide('deny')}>
          Deny
        </Button>
      </div>
    </div>
  );
}
